/**
 * Landing Page GraphQL Queries and Mutations
 *
 * Complete GraphQL API for tenant landing pages.
 *
 * FERP Principles:
 * - Repository pattern: All landing page access through GraphQL API
 * - camelCase naming: GraphQL fields follow FERP convention
 * - Backend ID generation: page_id from Snowflake system
 * - Data-driven: All landing page content from backend
 *
 * Issue: #312 - White-Label System Implementation (Phase 3)
 */

import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Fragments
// ============================================================================

export const LANDING_PAGE_FRAGMENT = gql`
  fragment LandingPageFragment on LandingPage {
    tenantId
    pageId
    status
    heroTitle
    heroSubtitle
    heroImageUrl
    heroCtaText
    heroCtaLink
    features {
      title
      description
      icon
      link
    }
    navigationItems {
      label
      link
      icon
      order
    }
    footerText
    footerLinks {
      label
      url
      order
    }
    layoutType
    showSearchBar
    showUserMenu
    pageTitle
    metaDescription
    metaKeywords
    createdAt
    updatedAt
    createdBy
    updatedBy
    version
  }
`;

// ============================================================================
// Queries
// ============================================================================

export const GET_LANDING_PAGE = gql`
  ${LANDING_PAGE_FRAGMENT}
  query GetLandingPage($tenantId: String!) {
    getLandingPage(tenantId: $tenantId) {
      ...LandingPageFragment
    }
  }
`;

export const GET_LANDING_PAGE_BY_ID = gql`
  ${LANDING_PAGE_FRAGMENT}
  query GetLandingPageById($tenantId: String!, $pageId: String!) {
    getLandingPageById(tenantId: $tenantId, pageId: $pageId) {
      ...LandingPageFragment
    }
  }
`;

export const LIST_LANDING_PAGES = gql`
  ${LANDING_PAGE_FRAGMENT}
  query ListLandingPages($tenantId: String!, $status: PageStatus) {
    listLandingPages(tenantId: $tenantId, status: $status) {
      ...LandingPageFragment
    }
  }
`;

export const LIST_PAGES_BY_LAYOUT = gql`
  ${LANDING_PAGE_FRAGMENT}
  query ListPagesByLayout($layoutType: LayoutType!) {
    listPagesByLayout(layoutType: $layoutType) {
      ...LandingPageFragment
    }
  }
`;

// ============================================================================
// Mutations
// ============================================================================

export const CREATE_LANDING_PAGE = gql`
  ${LANDING_PAGE_FRAGMENT}
  mutation CreateLandingPage($input: CreateLandingPageInput!) {
    createLandingPage(input: $input) {
      ...LandingPageFragment
    }
  }
`;

export const UPDATE_LANDING_PAGE = gql`
  ${LANDING_PAGE_FRAGMENT}
  mutation UpdateLandingPage($input: UpdateLandingPageInput!) {
    updateLandingPage(input: $input) {
      ...LandingPageFragment
    }
  }
`;

export const ACTIVATE_LANDING_PAGE = gql`
  ${LANDING_PAGE_FRAGMENT}
  mutation ActivateLandingPage($tenantId: String!, $pageId: String!) {
    activateLandingPage(tenantId: $tenantId, pageId: $pageId) {
      ...LandingPageFragment
    }
  }
`;

export const ARCHIVE_LANDING_PAGE = gql`
  ${LANDING_PAGE_FRAGMENT}
  mutation ArchiveLandingPage($tenantId: String!, $pageId: String!) {
    archiveLandingPage(tenantId: $tenantId, pageId: $pageId) {
      ...LandingPageFragment
    }
  }
`;

export const DELETE_LANDING_PAGE = gql`
  mutation DeleteLandingPage($tenantId: String!, $pageId: String!) {
    deleteLandingPage(tenantId: $tenantId, pageId: $pageId)
  }
`;

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Page Status Enum
 */
export enum PageStatus {
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Layout Type Enum
 */
export enum LayoutType {
  HEALTHCARE = 'HEALTHCARE',
  DENTAL = 'DENTAL',
  LEGAL = 'LEGAL',
  FINANCE = 'FINANCE',
}

/**
 * Feature
 */
export interface Feature {
  title: string;
  description: string;
  icon: string;
  link?: string | null;
}

/**
 * Navigation Item
 */
export interface NavItem {
  label: string;
  link: string;
  icon?: string | null;
  order: number;
}

/**
 * Footer Link
 */
export interface FooterLink {
  label: string;
  url: string;
  order: number;
}

/**
 * Landing Page
 */
export interface LandingPage {
  tenantId: string;
  pageId: string;
  status: PageStatus;
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl: string;
  heroCtaText: string;
  heroCtaLink: string;
  features: Feature[];
  navigationItems: NavItem[];
  footerText: string;
  footerLinks: FooterLink[];
  layoutType: LayoutType;
  showSearchBar: boolean;
  showUserMenu: boolean;
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
}

/**
 * Create Landing Page Input
 */
export interface CreateLandingPageInput {
  tenantId: string;
  layoutType: LayoutType;
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl: string;
  heroCtaText: string;
  heroCtaLink: string;
  features: FeatureInput[];
  navigationItems: NavItemInput[];
  footerText: string;
  footerLinks: FooterLinkInput[];
  showSearchBar: boolean;
  showUserMenu: boolean;
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string[];
}

/**
 * Update Landing Page Input
 */
export interface UpdateLandingPageInput {
  tenantId: string;
  pageId: string;
  heroTitle?: string | null;
  heroSubtitle?: string | null;
  heroImageUrl?: string | null;
  heroCtaText?: string | null;
  heroCtaLink?: string | null;
  features?: FeatureInput[] | null;
  navigationItems?: NavItemInput[] | null;
  footerText?: string | null;
  footerLinks?: FooterLinkInput[] | null;
  showSearchBar?: boolean | null;
  showUserMenu?: boolean | null;
  pageTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string[] | null;
}

/**
 * Feature Input
 */
export interface FeatureInput {
  title: string;
  description: string;
  icon: string;
  link?: string | null;
}

/**
 * Nav Item Input
 */
export interface NavItemInput {
  label: string;
  link: string;
  icon?: string | null;
  order: number;
}

/**
 * Footer Link Input
 */
export interface FooterLinkInput {
  label: string;
  url: string;
  order: number;
}

// ============================================================================
// Query/Mutation Result Types
// ============================================================================

export interface GetLandingPageData {
  getLandingPage: LandingPage | null;
}

export interface GetLandingPageVars {
  tenantId: string;
}

export interface GetLandingPageByIdData {
  getLandingPageById: LandingPage | null;
}

export interface GetLandingPageByIdVars {
  tenantId: string;
  pageId: string;
}

export interface ListLandingPagesData {
  listLandingPages: LandingPage[];
}

export interface ListLandingPagesVars {
  tenantId: string;
  status?: PageStatus | null;
}

export interface ListPagesByLayoutData {
  listPagesByLayout: LandingPage[];
}

export interface ListPagesByLayoutVars {
  layoutType: LayoutType;
}

export interface CreateLandingPageData {
  createLandingPage: LandingPage;
}

export interface CreateLandingPageVars {
  input: CreateLandingPageInput;
}

export interface UpdateLandingPageData {
  updateLandingPage: LandingPage;
}

export interface UpdateLandingPageVars {
  input: UpdateLandingPageInput;
}

export interface ActivateLandingPageData {
  activateLandingPage: LandingPage;
}

export interface ActivateLandingPageVars {
  tenantId: string;
  pageId: string;
}

export interface ArchiveLandingPageData {
  archiveLandingPage: LandingPage;
}

export interface ArchiveLandingPageVars {
  tenantId: string;
  pageId: string;
}

export interface DeleteLandingPageData {
  deleteLandingPage: boolean;
}

export interface DeleteLandingPageVars {
  tenantId: string;
  pageId: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for page status
 */
export function getStatusLabel(status: PageStatus): string {
  switch (status) {
    case PageStatus.ACTIVE:
      return 'Active';
    case PageStatus.DRAFT:
      return 'Draft';
    case PageStatus.ARCHIVED:
      return 'Archived';
    default:
      return 'Unknown';
  }
}

/**
 * Get color for page status badge
 */
export function getStatusColor(status: PageStatus): string {
  switch (status) {
    case PageStatus.ACTIVE:
      return 'green';
    case PageStatus.DRAFT:
      return 'yellow';
    case PageStatus.ARCHIVED:
      return 'gray';
    default:
      return 'gray';
  }
}

/**
 * Get display label for layout type
 */
export function getLayoutLabel(layout: LayoutType): string {
  switch (layout) {
    case LayoutType.HEALTHCARE:
      return 'Healthcare';
    case LayoutType.DENTAL:
      return 'Dental';
    case LayoutType.LEGAL:
      return 'Legal';
    case LayoutType.FINANCE:
      return 'Finance';
    default:
      return 'Unknown';
  }
}

/**
 * Get icon for layout type
 */
export function getLayoutIcon(layout: LayoutType): string {
  switch (layout) {
    case LayoutType.HEALTHCARE:
      return 'IconHeartbeat';
    case LayoutType.DENTAL:
      return 'IconDental';
    case LayoutType.LEGAL:
      return 'IconScale';
    case LayoutType.FINANCE:
      return 'IconCoin';
    default:
      return 'IconLayout';
  }
}

/**
 * Get color for layout type
 */
export function getLayoutColor(layout: LayoutType): string {
  switch (layout) {
    case LayoutType.HEALTHCARE:
      return 'blue';
    case LayoutType.DENTAL:
      return 'teal';
    case LayoutType.LEGAL:
      return 'violet';
    case LayoutType.FINANCE:
      return 'green';
    default:
      return 'gray';
  }
}

/**
 * Validate hero section
 */
export function validateHeroSection(hero: {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
}): string[] {
  const errors: string[] = [];

  if (!hero.title || hero.title.trim().length === 0) {
    errors.push('Hero title is required');
  }
  if (!hero.subtitle || hero.subtitle.trim().length === 0) {
    errors.push('Hero subtitle is required');
  }
  if (!hero.ctaText || hero.ctaText.trim().length === 0) {
    errors.push('Hero CTA text is required');
  }
  if (!hero.ctaLink || hero.ctaLink.trim().length === 0) {
    errors.push('Hero CTA link is required');
  }

  return errors;
}

/**
 * Validate SEO metadata
 */
export function validateSEO(seo: {
  pageTitle: string;
  metaDescription: string;
}): string[] {
  const errors: string[] = [];

  if (!seo.pageTitle || seo.pageTitle.trim().length === 0) {
    errors.push('Page title is required');
  }
  if (!seo.metaDescription || seo.metaDescription.trim().length === 0) {
    errors.push('Meta description is required');
  }
  if (seo.metaDescription && seo.metaDescription.length > 160) {
    errors.push('Meta description should be under 160 characters');
  }

  return errors;
}
